import { useCallback, useEffect, useRef, useState } from "react";
import { NOOP } from "~/constants/callbacks";
import { clearUrlParams } from "~/helpers/locationHelper";

const SCROLL_CONFIG = {
  HIGHLIGHT_DURATION: 1500,
  // Must be ≥ CSS transition duration (0.9s on .timeline-activity /
  // .message-content) PLUS a buffer for setTimeout drift, otherwise
  // the fading class is stripped before the transition has actually
  // reached 0 and the last few percent visibly cliff-drop instead of
  // fading. 300ms buffer is comfortable.
  HIGHLIGHT_FADE_DURATION: 1200,
  MAX_ALIGN_ATTEMPTS: 3,
  MAX_RENDER_ATTEMPTS: 8,
  RENDER_RETRY_DELAY_MS: 80,
  ALIGN_TOP_THRESHOLD_PX: 24,
};

const areSetsEqual = (left, right) => {
  if (left.size !== right.size) {
    return false;
  }

  for (const value of left) {
    if (!right.has(value)) {
      return false;
    }
  }

  return true;
};

// Tracks the set of item IDs currently mounted by Virtuoso so the
// retry loop knows when a new render has actually given us a chance
// to find the anchor. Without this, retries fire on a stale signal.
const useRenderedItemIds = () => {
  const renderedItemIds = useRef(new Set());
  const [renderedItemsSignal, setRenderedItemsSignal] = useState(0);

  const handleItemsRendered = useCallback((renderedItems) => {
    const nextRenderedItemIds = new Set(
      renderedItems.map((item) => item?.data?.id).filter((id) => id != null),
    );

    if (areSetsEqual(renderedItemIds.current, nextRenderedItemIds)) {
      return;
    }

    renderedItemIds.current = nextRenderedItemIds;
    setRenderedItemsSignal((value) => value + 1);
  }, []);

  return {
    renderedItemIds,
    renderedItemsSignal,
    handleItemsRendered,
  };
};

// Pauses auto-fetch of the next page until the user actually
// interacts with the scroller — without this the InfiniteScroll's
// endReached can fire immediately on initial mount when the anchor
// lands near the bottom of its page, causing the next page to load
// underneath the user before they scroll, defeating the centering.
const useResumePaginationAfterInteraction = ({
  containerRef,
  initialAnchorIdRef,
  resetKey,
  nextPage,
  loading,
  loadMore,
}) => {
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  useEffect(() => {
    setHasUserInteracted(false);
  }, [resetKey]);

  const handleUserInteraction = useCallback(
    (event) => {
      if (initialAnchorIdRef.current == null || hasUserInteracted) {
        return;
      }

      setHasUserInteracted(true);

      if (
        event?.type === "wheel" &&
        event.deltaY > 0 &&
        nextPage != null &&
        !loading
      ) {
        loadMore("next");
      }
    },
    [hasUserInteracted, initialAnchorIdRef, nextPage, loading, loadMore],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    container.addEventListener("wheel", handleUserInteraction, {
      passive: true,
    });
    container.addEventListener("touchstart", handleUserInteraction, {
      passive: true,
    });
    container.addEventListener("mousedown", handleUserInteraction);

    return () => {
      container.removeEventListener("wheel", handleUserInteraction);
      container.removeEventListener("touchstart", handleUserInteraction);
      container.removeEventListener("mousedown", handleUserInteraction);
    };
  }, [containerRef, handleUserInteraction]);

  return initialAnchorIdRef.current != null && !hasUserInteracted;
};

// Generic anchor-scroll machinery shared by the inbox messages thread
// and the lead-drawer activity timeline. Centring an arbitrary item
// inside a Virtuoso scroller reliably needs more care than
// `scrollToItem` alone — windowed rows may not be mounted yet, and
// Virtuoso resizes after the first paint. This hook handles:
//
//   * waiting for the anchor's row to mount (bounded retry)
//   * post-paint alignment via a two-frame rAF dance (bounded retry)
//   * a soft hold + fade highlight state machine
//   * URL-param cleanup once the scroll lands
//   * prev-page priming so the anchor isn't pinned at the very top
//   * pause-auto-next-page until the user interacts
//   * full reset + cleanup on resetKey change
//
// Caller responsibilities:
//   * render each item's wrapper with `id={`${idPrefix}-${item.id}`}`
//   * pass `infiniteScrollRef` to InfiniteScroll's `ref`
//   * pass `containerRef` to the surrounding scroll container
//   * pass `handleItemsRendered` to InfiniteScroll's `onItemsRendered`
//   * apply `highlightedItemId` / `isHighlightFading` to each item
const useAnchorScroll = ({
  anchorId,
  resetKey,
  items,
  loading,
  idPrefix,
  urlParamsToClear,
  prevPage = null,
  nextPage = null,
  loadMore = NOOP,
  // Pre-flight check; return false to suppress the scroll attempt
  // (e.g. when the anchor target is privacy-restricted). The hook
  // marks the scroll as completed and clears the URL params anyway,
  // so the caller can use this to surface its own user-facing
  // notification.
  shouldAttemptAnchor = () => true,
}) => {
  const infiniteScrollRef = useRef(null);
  const containerRef = useRef(null);
  const hasAutoScrolled = useRef(false);
  const isAnchorAlignmentInProgress = useRef(false);
  const anchorAlignAttempts = useRef(0);
  const anchorRenderAttempts = useRef(0);
  const hasPrimedPrev = useRef(false);
  const isAwaitingPrevPrime = useRef(false);
  const prevPrimeItemsLength = useRef(0);
  const prevPrimePage = useRef(null);
  const scrollTimeoutsRef = useRef([]);
  const initialAnchorIdRef = useRef(anchorId);
  const [highlightedItemId, setHighlightedItemId] = useState(null);
  const [isHighlightFading, setIsHighlightFading] = useState(false);
  const [anchorScrollSignal, setAnchorScrollSignal] = useState(0);
  const { renderedItemIds, renderedItemsSignal, handleItemsRendered } =
    useRenderedItemIds();
  const shouldPauseAutoNextPage = useResumePaginationAfterInteraction({
    containerRef,
    initialAnchorIdRef,
    resetKey,
    nextPage,
    loading,
    loadMore,
  });

  const clearAllTimeouts = () => {
    scrollTimeoutsRef.current.forEach((item) => {
      if (typeof item === "number") {
        clearTimeout(item);
      } else if (item?.cancel) {
        item.cancel();
      }
    });
    scrollTimeoutsRef.current = [];
  };

  useEffect(() => {
    clearAllTimeouts();

    hasAutoScrolled.current = false;
    isAnchorAlignmentInProgress.current = false;
    anchorAlignAttempts.current = 0;
    anchorRenderAttempts.current = 0;
    hasPrimedPrev.current = false;
    isAwaitingPrevPrime.current = false;
    prevPrimeItemsLength.current = 0;
    prevPrimePage.current = null;
    setHighlightedItemId(null);
    setIsHighlightFading(false);
    setAnchorScrollSignal((value) => value + 1);
    initialAnchorIdRef.current = anchorId;

    return clearAllTimeouts;
  }, [resetKey]);

  useEffect(() => {
    if (
      initialAnchorIdRef.current != null &&
      !hasPrimedPrev.current &&
      !loading &&
      prevPage != null &&
      items.length > 0
    ) {
      hasPrimedPrev.current = true;
      isAwaitingPrevPrime.current = true;
      prevPrimeItemsLength.current = items.length;
      prevPrimePage.current = prevPage;
      loadMore("prev");
    }
  }, [loading, prevPage, items.length, loadMore]);

  useEffect(() => {
    if (
      isAwaitingPrevPrime.current &&
      !loading &&
      (items.length !== prevPrimeItemsLength.current ||
        prevPage !== prevPrimePage.current)
    ) {
      isAwaitingPrevPrime.current = false;
      setAnchorScrollSignal((value) => value + 1);
    }
  }, [loading, items.length, prevPage]);

  const isAnchorAlignedToScrollerTop = useCallback((anchorEl) => {
    const scroller =
      anchorEl?.closest?.('[data-virtuoso-scroller="true"]') ||
      document.querySelector('[data-virtuoso-scroller="true"]');
    if (!scroller || !anchorEl) {
      return true;
    }

    const anchorTop = anchorEl.getBoundingClientRect().top;
    const scrollerTop = scroller.getBoundingClientRect().top;
    return (
      Math.abs(anchorTop - scrollerTop) <= SCROLL_CONFIG.ALIGN_TOP_THRESHOLD_PX
    );
  }, []);

  const completeAnchorScroll = useCallback(
    (settledAnchorId) => {
      hasAutoScrolled.current = true;
      isAnchorAlignmentInProgress.current = false;
      anchorRenderAttempts.current = 0;

      setHighlightedItemId(settledAnchorId);
      setIsHighlightFading(false);
      const highlightTimeout = setTimeout(() => {
        setIsHighlightFading(true);

        const fadeTimeout = setTimeout(() => {
          setHighlightedItemId(null);
          setIsHighlightFading(false);
        }, SCROLL_CONFIG.HIGHLIGHT_FADE_DURATION);
        scrollTimeoutsRef.current.push(fadeTimeout);
      }, SCROLL_CONFIG.HIGHLIGHT_DURATION);
      scrollTimeoutsRef.current.push(highlightTimeout);
      clearUrlParams(urlParamsToClear);
    },
    [urlParamsToClear],
  );

  const alignAnchorAfterPaint = useCallback(
    (settledAnchorId, onComplete) => {
      const alignAnchor = () => {
        const anchorEl = document.getElementById(
          `${idPrefix}-${settledAnchorId}`,
        );
        anchorEl?.scrollIntoView?.({
          block: "start",
          behavior: "auto",
        });
      };

      let firstFrame = null;
      let secondFrame = null;
      const requestFrame =
        window.requestAnimationFrame ||
        globalThis.requestAnimationFrame ||
        ((callback) => setTimeout(callback, 0));
      const cancelFrame =
        window.cancelAnimationFrame ||
        globalThis.cancelAnimationFrame ||
        clearTimeout;

      firstFrame = requestFrame(() => {
        alignAnchor();
        secondFrame = requestFrame(() => {
          alignAnchor();
          onComplete?.();
        });
      });

      scrollTimeoutsRef.current.push({
        cancel: () => {
          if (firstFrame != null) {
            cancelFrame(firstFrame);
          }
          if (secondFrame != null) {
            cancelFrame(secondFrame);
          }
        },
      });
    },
    [idPrefix],
  );

  useEffect(() => {
    const settledAnchorId = initialAnchorIdRef.current;

    if (
      settledAnchorId == null ||
      items.length === 0 ||
      hasAutoScrolled.current ||
      loading ||
      isAwaitingPrevPrime.current
    ) {
      return;
    }

    if (!shouldAttemptAnchor()) {
      hasAutoScrolled.current = true;
      clearUrlParams(urlParamsToClear);
      return;
    }

    const index = items.findIndex((item) => item?.id === settledAnchorId);

    if (index === -1) {
      return;
    }

    const anchorEl = document.getElementById(`${idPrefix}-${settledAnchorId}`);
    const isAnchorAvailable =
      renderedItemIds.current.has(settledAnchorId) || Boolean(anchorEl);

    if (!isAnchorAvailable) {
      if (anchorRenderAttempts.current >= SCROLL_CONFIG.MAX_RENDER_ATTEMPTS) {
        completeAnchorScroll(settledAnchorId);
        return;
      }

      anchorRenderAttempts.current += 1;
      infiniteScrollRef.current?.scrollToItem(index, "start");

      const retryTimeout = setTimeout(() => {
        setAnchorScrollSignal((value) => value + 1);
      }, SCROLL_CONFIG.RENDER_RETRY_DELAY_MS);
      scrollTimeoutsRef.current.push(retryTimeout);

      return;
    }

    if (isAnchorAlignmentInProgress.current) {
      return;
    }

    if (!anchorEl) {
      return;
    }

    isAnchorAlignmentInProgress.current = true;
    anchorAlignAttempts.current += 1;
    alignAnchorAfterPaint(settledAnchorId, () => {
      const anchorAfterAlign = document.getElementById(
        `${idPrefix}-${settledAnchorId}`,
      );
      const hasAnchorInRenderedSet =
        renderedItemIds.current.has(settledAnchorId);
      const isAligned = isAnchorAlignedToScrollerTop(anchorAfterAlign);

      if (anchorAfterAlign && hasAnchorInRenderedSet && isAligned) {
        completeAnchorScroll(settledAnchorId);
        return;
      }

      if (anchorAlignAttempts.current >= SCROLL_CONFIG.MAX_ALIGN_ATTEMPTS) {
        completeAnchorScroll(settledAnchorId);
        return;
      }

      isAnchorAlignmentInProgress.current = false;
      setAnchorScrollSignal((value) => value + 1);
    });
  }, [
    items,
    loading,
    anchorScrollSignal,
    renderedItemsSignal,
    alignAnchorAfterPaint,
    completeAnchorScroll,
    isAnchorAlignedToScrollerTop,
    idPrefix,
    urlParamsToClear,
    shouldAttemptAnchor,
    renderedItemIds,
  ]);

  return {
    infiniteScrollRef,
    containerRef,
    highlightedItemId,
    isHighlightFading,
    handleItemsRendered,
    shouldPauseAutoNextPage,
  };
};

export default useAnchorScroll;
